import type { FC } from 'react';
import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Typography } from '@interstate/components/Typography';
import { SlideOutHeaderContainer, SlideOutSubtitle } from './MileageCostOptions.style';

import * as offerReduxSelectors from '@makemydeal/dr-dash-store/selectors/offerRedux';
import * as offerSelectors from '@makemydeal/dr-dash-store/selectors/offerSelectors';
import * as vehicleSelectors from '@makemydeal/dr-dash-store/selectors/vehicleSelectors';
import { parseSelectedTermMonths } from '../terms/common/utils';
import { getDisplayOfferType } from '../utils/formatUtils';
import { formatDollarsAndCents } from '@makemydeal/dr-common-utils';

import { SLIDEOUT_TITLE } from './constants';

export const MileageCostOptionsHeader: FC = () => {
    // Vehicle info for header subtitle
    const vehicleCondition = useSelector(vehicleSelectors.getVehicleCondition);
    const vehicleYear = useSelector(vehicleSelectors.getVehicleYear);
    const vehicleMake = useSelector(vehicleSelectors.getVehicleMake);
    const vehicleModel = useSelector(vehicleSelectors.getVehicleModel);
    const vehicleTrim = useSelector(vehicleSelectors.getVehicleTrim);
    const currentOfferType = useSelector(offerReduxSelectors.getCurrentOfferType);
    const monthlyPayment = useSelector(offerSelectors.getMonthlyPayment);
    const apr = useSelector(offerReduxSelectors.getApr);
    const rateType = useSelector(offerReduxSelectors.getRateType);
    const moneyFactor = useSelector(offerReduxSelectors.getMoneyFactor);
    const selectedTermId = useSelector(offerReduxSelectors.getTermIdByOfferType);

    const terms = useMemo(() => parseSelectedTermMonths(selectedTermId), [selectedTermId]);

    // Build header subtitle
    const subtitle = useMemo(() => {
        const condition = vehicleCondition || '';
        const displayOfferType = getDisplayOfferType(currentOfferType);
        const formattedPayment = formatDollarsAndCents(monthlyPayment);

        // Display rate in native format based on rate type
        const isMoneyFactor = rateType === 'MoneyFactor';
        const rateValue = isMoneyFactor ? moneyFactor : apr;
        const formattedRate = isMoneyFactor ? rateValue?.toFixed(5) || '0.00000' : rateValue?.toFixed(1) || '0.0';
        const rateLabel = isMoneyFactor ? 'MF' : '%APR';

        return `${condition} ${vehicleYear} ${vehicleMake} ${vehicleModel} ${vehicleTrim} - ${displayOfferType} ${terms}, ${formattedPayment}/mo - ${formattedRate}${rateLabel}`.trim();
    }, [
        vehicleCondition,
        vehicleYear,
        vehicleMake,
        vehicleModel,
        vehicleTrim,
        currentOfferType,
        terms,
        monthlyPayment,
        apr,
        rateType,
        moneyFactor
    ]);

    return (
        <SlideOutHeaderContainer>
            <Typography variant="h3" data-testid="mileage-cost-options-header-title">
                {SLIDEOUT_TITLE}
            </Typography>
            <SlideOutSubtitle variant="body-sm" data-testid="mileage-cost-options-header-subtitle">
                {subtitle}
            </SlideOutSubtitle>
        </SlideOutHeaderContainer>
    );
};

/* istanbul ignore next */
export default MileageCostOptionsHeader;
